import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Package, Truck, User, Box, AlertTriangle, Building2 } from 'lucide-react';

export default function DeliveryInfoDialog({ 
  open, 
  onClose, 
  onSubmit,
  onSubmitWithoutSignature,
  customerName,
  skipPackagingQuestion 
}) {
  const [step, setStep] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [packagingType, setPackagingType] = useState('');
  const [paletteCount, setPaletteCount] = useState(1);
  const [rahmenCount, setRahmenCount] = useState(0);
  const [euroboxCount, setEuroboxCount] = useState(1);
  const [isExchange, setIsExchange] = useState(false);
  const [courierService, setCourierService] = useState('');
  const [courierCost, setCourierCost] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [showLicensePlateWarning, setShowLicensePlateWarning] = useState(false);

  const resetForm = () => {
    setStep(1);
    setDeliveryMethod('');
    setPackagingType('');
    setPaletteCount(1);
    setRahmenCount(0);
    setEuroboxCount(1);
    setIsExchange(false);
    setCourierService('');
    setCourierCost('');
    setLicensePlate('');
    setShowLicensePlateWarning(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const buildDeliveryInfo = () => {
    let packagingCount = null;
    
    if (packagingType === 'palette') {
      // Palette includes rahmen count
      packagingCount = { paletten: paletteCount, rahmen: rahmenCount };
    } else if (packagingType === 'eurobox') {
      packagingCount = euroboxCount;
    }

    return {
      delivery_method: deliveryMethod,
      packaging_type: skipPackagingQuestion ? 'none' : packagingType,
      packaging_count: packagingCount,
      is_exchange: isExchange,
      courier_service: deliveryMethod === 'paketdienst' ? courierService : null,
      courier_cost: deliveryMethod === 'paketdienst' && courierCost ? parseFloat(courierCost) : null,
      license_plate: deliveryMethod === 'paketdienst' ? licensePlate : null,
      delivery_date: new Date().toISOString().split('T')[0]
    };
  };

  // Schritt 1: Versandart
  const handleDeliveryMethodNext = () => {
    if (deliveryMethod === 'paketdienst') {
      setStep(2); // Zu Paketdienst-Details
    } else if (skipPackagingQuestion) {
      // Direkt zu Unterschrift
      onSubmit(buildDeliveryInfo());
    } else {
      setStep(2); // Zu Verpackung
    }
  };

  // Bei Paketdienst: Speichern ohne Unterschrift
  const handlePaketdienstSubmit = () => {
    if (!courierService.trim()) {
      return;
    }
    if (!licensePlate.trim()) {
      setShowLicensePlateWarning(true);
      return;
    }
    const deliveryInfo = buildDeliveryInfo();
    onSubmitWithoutSignature(deliveryInfo);
    resetForm();
  };

  const handlePaketdienstSubmitWithoutPlate = () => {
    setShowLicensePlateWarning(false);
    const deliveryInfo = buildDeliveryInfo();
    onSubmitWithoutSignature(deliveryInfo);
    resetForm();
  };

  // Schritt 2 (bei Abholung/Fahrdienst/Metaxon): Verpackung
  const handlePackagingNext = () => {
    if (packagingType === 'palette') {
      setStep(3); // Zu Palette-Anzahl
    } else if (packagingType === 'eurobox') {
      setStep(3); // Zu Eurobox-Anzahl
    } else {
      // Karton oder ohne - direkt zu Unterschrift
      onSubmit(buildDeliveryInfo());
    }
  };

  // Schritt 3: Palette Anzahl -> Rahmen Anzahl
  const handlePaletteCountNext = () => {
    setStep(4); // Zu Rahmen-Anzahl
  };

  // Schritt 4: Rahmen Anzahl -> Unterschrift
  const handleRahmenCountNext = () => {
    onSubmit(buildDeliveryInfo());
  };

  // Schritt 3 (Eurobox): Anzahl -> Unterschrift
  const handleEuroboxCountNext = () => {
    onSubmit(buildDeliveryInfo());
  };

  const getStepCount = () => {
    if (deliveryMethod === 'paketdienst') return 2;
    if (skipPackagingQuestion) return 1;
    if (packagingType === 'palette') return 4; // Versandart -> Verpackung -> Paletten -> Rahmen
    if (packagingType === 'eurobox') return 3;
    return 2;
  };

  // Render: Schritt 1 - Versandart
  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Wie wurde geliefert?</h3>
      <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod}>
        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="abholung" id="abholung" />
          <Label htmlFor="abholung" className="flex items-center space-x-2 cursor-pointer flex-1">
            <User className="h-5 w-5 text-blue-500" />
            <span>Abholung durch Kunden</span>
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="metaxon" id="metaxon" />
          <Label htmlFor="metaxon" className="flex items-center space-x-2 cursor-pointer flex-1">
            <Building2 className="h-5 w-5 text-purple-500" />
            <span>Lieferung durch Metaxon AG</span>
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="fahrdienst" id="fahrdienst" />
          <Label htmlFor="fahrdienst" className="flex items-center space-x-2 cursor-pointer flex-1">
            <Truck className="h-5 w-5 text-green-500" />
            <span>Lieferung durch Fahrdienst</span>
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="paketdienst" id="paketdienst" />
          <Label htmlFor="paketdienst" className="flex items-center space-x-2 cursor-pointer flex-1">
            <Package className="h-5 w-5 text-orange-500" />
            <div>
              <span>Versand per Paketdienst</span>
              <p className="text-xs text-gray-500">Keine Unterschrift erforderlich</p>
            </div>
          </Label>
        </div>
      </RadioGroup>
    </div>
  );

  // Render: Paketdienst-Details
  const renderPaketdienstDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Paketdienst-Details</h3>
      
      <div className="space-y-2">
        <Label htmlFor="courier">Lieferdienst *</Label>
        <Input
          id="courier"
          value={courierService}
          onChange={(e) => setCourierService(e.target.value)}
          placeholder="z.B. DHL, UPS, DPD, Hermes"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="plate">Kennzeichen (optional)</Label>
        <Input
          id="plate"
          value={licensePlate}
          onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
          placeholder="z.B. AC-XX-1234"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost">Lieferkosten (€)</Label>
        <Input
          id="cost"
          type="number"
          step="0.01"
          value={courierCost}
          onChange={(e) => setCourierCost(e.target.value)}
          placeholder="z.B. 15.50"
        />
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Hinweis:</strong> Bei Versand per Paketdienst ist keine Unterschrift erforderlich. 
          Der Lieferschein wird als "Versendet" markiert.
        </p>
      </div>
    </div>
  );

  // Render: Schritt 2 - Verpackung
  const renderPackaging = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Welche Verpackung wurde verwendet?</h3>
      <RadioGroup value={packagingType} onValueChange={setPackagingType}>
        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="palette" id="palette" />
          <Label htmlFor="palette" className="flex items-center space-x-2 cursor-pointer flex-1">
            <Box className="h-5 w-5 text-amber-600" />
            <span>Palette (mit Aufsatzrahmen)</span>
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="eurobox" id="eurobox" />
          <Label htmlFor="eurobox" className="flex items-center space-x-2 cursor-pointer flex-1">
            <Box className="h-5 w-5 text-blue-600" />
            <span>Eurobox</span>
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="karton" id="karton" />
          <Label htmlFor="karton" className="flex items-center space-x-2 cursor-pointer flex-1">
            <Package className="h-5 w-5 text-gray-600" />
            <span>Karton</span>
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
          <RadioGroupItem value="ohne" id="ohne" />
          <Label htmlFor="ohne" className="flex items-center space-x-2 cursor-pointer flex-1">
            <span className="h-5 w-5" />
            <span>Ohne Verpackung</span>
          </Label>
        </div>
      </RadioGroup>
    </div>
  );

  // Render: Schritt 3 - Palette Anzahl
  const renderPaletteCount = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Anzahl Paletten</h3>
      
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <Button
            key={num}
            type="button"
            variant={paletteCount === num ? 'default' : 'outline'}
            className={paletteCount === num ? 'bg-amber-600 hover:bg-amber-700' : ''}
            onClick={() => setPaletteCount(num)}
          >
            {num}
          </Button>
        ))}
      </div>

      <div className="flex items-center space-x-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <Checkbox
          id="exchange-palette"
          checked={isExchange}
          onCheckedChange={setIsExchange}
        />
        <Label htmlFor="exchange-palette" className="cursor-pointer">
          Getauscht (nicht beim Kunden hinterlegen)
        </Label>
      </div>
    </div>
  );

  // Render: Schritt 4 - Rahmen Anzahl
  const renderRahmenCount = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Anzahl Aufsatzrahmen</h3>
      
      <div className="flex flex-wrap gap-2">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <Button
            key={num}
            type="button"
            variant={rahmenCount === num ? 'default' : 'outline'}
            className={rahmenCount === num ? 'bg-amber-800 hover:bg-amber-900' : ''}
            onClick={() => setRahmenCount(num)}
          >
            {num}
          </Button>
        ))}
      </div>

      <div className="flex items-center space-x-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <Checkbox
          id="exchange-rahmen"
          checked={isExchange}
          onCheckedChange={setIsExchange}
        />
        <Label htmlFor="exchange-rahmen" className="cursor-pointer">
          Getauscht (nicht beim Kunden hinterlegen)
        </Label>
      </div>

      {!isExchange && (paletteCount > 0 || rahmenCount > 0) && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <p className="font-semibold text-blue-900">Info:</p>
          <p className="text-blue-800">
            {paletteCount} Palette(n) + {rahmenCount} Aufsatzrahmen werden dem Konto von <strong>{customerName}</strong> hinzugefügt.
          </p>
        </div>
      )}
    </div>
  );

  // Render: Schritt 3 - Eurobox Anzahl
  const renderEuroboxCount = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Anzahl Euroboxen</h3>
      
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <Button
            key={num}
            type="button"
            variant={euroboxCount === num ? 'default' : 'outline'}
            className={euroboxCount === num ? 'bg-blue-600 hover:bg-blue-700' : ''}
            onClick={() => setEuroboxCount(num)}
          >
            {num}
          </Button>
        ))}
      </div>

      <div className="flex items-center space-x-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <Checkbox
          id="exchange-eurobox"
          checked={isExchange}
          onCheckedChange={setIsExchange}
        />
        <Label htmlFor="exchange-eurobox" className="cursor-pointer">
          Getauscht (nicht beim Kunden hinterlegen)
        </Label>
      </div>

      {!isExchange && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <p className="font-semibold text-blue-900">Info:</p>
          <p className="text-blue-800">
            {euroboxCount} Eurobox(en) werden dem Konto von <strong>{customerName}</strong> hinzugefügt.
          </p>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    if (step === 1) {
      return renderStep1();
    }
    
    if (deliveryMethod === 'paketdienst' && step === 2) {
      return renderPaketdienstDetails();
    }
    
    if (step === 2) {
      return renderPackaging();
    }
    
    if (step === 3 && packagingType === 'palette') {
      return renderPaletteCount();
    }
    
    if (step === 3 && packagingType === 'eurobox') {
      return renderEuroboxCount();
    }
    
    if (step === 4 && packagingType === 'palette') {
      return renderRahmenCount();
    }
    
    return null;
  };

  const renderFooter = () => {
    // Schritt 1: Versandart
    if (step === 1) {
      return (
        <Button onClick={handleDeliveryMethodNext} disabled={!deliveryMethod}>
          Weiter
        </Button>
      );
    }

    // Paketdienst-Details
    if (deliveryMethod === 'paketdienst' && step === 2) {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(1)}>
            Zurück
          </Button>
          <Button onClick={handlePaketdienstSubmit} disabled={!courierService.trim()}>
            Speichern (ohne Unterschrift)
          </Button>
        </>
      );
    }

    // Verpackung (Schritt 2)
    if (step === 2) {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(1)}>
            Zurück
          </Button>
          <Button onClick={handlePackagingNext} disabled={!packagingType}>
            {packagingType === 'karton' || packagingType === 'ohne' ? 'Weiter zur Unterschrift' : 'Weiter'}
          </Button>
        </>
      );
    }

    // Palette Anzahl (Schritt 3)
    if (step === 3 && packagingType === 'palette') {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(2)}>
            Zurück
          </Button>
          <Button onClick={handlePaletteCountNext}>
            Weiter zu Aufsatzrahmen
          </Button>
        </>
      );
    }

    // Eurobox Anzahl (Schritt 3)
    if (step === 3 && packagingType === 'eurobox') {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(2)}>
            Zurück
          </Button>
          <Button onClick={handleEuroboxCountNext}>
            Weiter zur Unterschrift
          </Button>
        </>
      );
    }

    // Rahmen Anzahl (Schritt 4)
    if (step === 4 && packagingType === 'palette') {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(3)}>
            Zurück
          </Button>
          <Button onClick={handleRahmenCountNext}>
            Weiter zur Unterschrift
          </Button>
        </>
      );
    }

    return null;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Lieferinformation erfassen
              <span className="text-sm font-normal text-gray-500 ml-2">
                Schritt {step} von {getStepCount()}
              </span>
            </DialogTitle>
          </DialogHeader>

          {renderCurrentStep()}

          <DialogFooter>
            {renderFooter()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kennzeichen-Warnung Dialog */}
      <AlertDialog open={showLicensePlateWarning} onOpenChange={setShowLicensePlateWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Kein Kennzeichen eingegeben
            </AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie wirklich ohne Kennzeichen fortfahren?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowLicensePlateWarning(false)}>
              Zurück
            </AlertDialogCancel>
            <AlertDialogAction onClick={handlePaketdienstSubmitWithoutPlate}>
              Ja, ohne Kennzeichen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
